import React, { useState } from 'react';
import { Offcanvas, Spinner, Button, Tabs, Tab } from 'react-bootstrap';
import { IconArrowMerge } from '@tabler/icons-react';

// Import hook dan komponen V4
import { useShowBookingV4 } from '../../../../services/create-booking-v4/hooks/useShowBookingV4';
import CreateBookingComponentV4 from '../create-reservation-v4/CreateBookingComponentV4';
import MergeReservationModal from '../../../pms-calendar/components/calendar/MergeReservationModal';
import ReadOnlyGuard from '../../../../shared/components/ReadOnlyGuard';
import ReservationLogHistoryTab from './ReservationLogHistoryTab';

// Tipe props untuk komponen ini
interface UpdateReservationProps {
    show: boolean;
    onClose: () => void;
    bookingId: string;
}

const UpdateReservation: React.FC<UpdateReservationProps> = ({ show, onClose, bookingId }) => {
    // Panggil hook untuk mengambil data booking
    const { data: bookingData, isLoading, isError, error } = useShowBookingV4({
        bookingId: bookingId,
        include: "property,roomType,room,ratePlan"
    });

    const [isProcessing, setIsProcessing] = useState(false);
    const [showMergeModal, setShowMergeModal] = useState(false);

    const handleClose = () => {
        // Jangan izinkan menutup jika sedang dalam proses
        if (isProcessing) return;
        onClose();
    };

    // Kamar yang sedang diedit di layar ini -- dipakai untuk menentukan apakah
    // tombol Merge tampil (hanya untuk segmen split, ditandai `split_role`).
    const currentRoom = bookingData?.data?.booking.find((room) => room.id === bookingId);

    return (
        <Offcanvas
            show={show}
            placement="end"
            onHide={handleClose}
            style={{ width: '100%' }}
            // Kita bisa membuat Offcanvas tidak bisa ditutup saat loading
            backdrop={isLoading || isProcessing ? 'static' : true}
            keyboard={!isLoading && !isProcessing}
        >
            <Offcanvas.Header closeButton={!isLoading}>
                <Offcanvas.Title>Update Reservation</Offcanvas.Title>
                {currentRoom?.split_role && (
                    <ReadOnlyGuard>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-3"
                            onClick={() => setShowMergeModal(true)}
                        >
                            <IconArrowMerge size={14} className="me-1" /> Merge
                        </Button>
                    </ReadOnlyGuard>
                )}
            </Offcanvas.Header>
            <Offcanvas.Body>
                {isLoading && (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <Spinner animation="border" variant="primary" />
                        <span className="ms-3">Loading Reservation Data...</span>
                    </div>
                )}

                {isError && (
                    <div className="alert alert-danger">
                        <h4 className="alert-heading">Failed to load data</h4>
                        <p>There was an error fetching the reservation details. Please try again.</p>
                        <hr />
                        <p className="mb-0 small">Error: {error.message}</p>
                    </div>
                )}

                {/* Render form hanya jika data berhasil diambil */}
                {!isLoading && !isError && bookingData?.data && (
                    <Tabs defaultActiveKey="details" className="mb-3">
                        <Tab eventKey="details" title="Details">
                            <CreateBookingComponentV4
                                onClose={handleClose}
                                initialData={bookingData.data}
                                onProcessingChange={setIsProcessing}
                            />
                        </Tab>
                        <Tab eventKey="log-history" title="Log History">
                            <ReservationLogHistoryTab bookingId={bookingId} />
                        </Tab>
                    </Tabs>
                )}
            </Offcanvas.Body>

            {showMergeModal && (
                <ReadOnlyGuard mode="hide">
                    <MergeReservationModal
                        show={showMergeModal}
                        onClose={() => setShowMergeModal(false)}
                        bookingId={bookingId}
                    />
                </ReadOnlyGuard>
            )}
        </Offcanvas>
    );
};

export default UpdateReservation;